/**
 * @file taskService.ts
 * @brief Task API service. Uses backend /task_management/tasks; returns unwrapped payloads.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type {
  Task,
  TaskStatus,
  TaskFilters,
  PaginatedResponse,
  TaskComment,
  TaskRACI,
  Attachment,
  LinkedIssue,
  DueDateChange,
  ChecklistItem,
  RecurrenceConfig,
  RecurrenceInstance,
  RecurrenceFrequency,
  RecurrenceEndType,
} from '@/types';

const defaultRaci: TaskRACI = { responsible: [], accountable: '', consulted: [], informed: [] };

/** Normalize to date-only YYYY-MM-DD for MySQL DATE/DATETIME columns. */
function toDateOnly(value: string | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  const s = typeof value === 'string' ? value : String(value);
  return s.slice(0, 10);
}

function mapRawToLinkedIssue(raw: Record<string, unknown>): LinkedIssue {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    provider: (raw.provider as LinkedIssue['provider']) ?? 'internal',
    externalId: raw.externalId != null ? String(raw.externalId) : undefined,
    externalUrl: raw.externalUrl != null ? String(raw.externalUrl) : undefined,
    status: (raw.status as LinkedIssue['status']) ?? 'open',
    priority: (raw.priority as LinkedIssue['priority']) ?? 'medium',
    type: (raw.type as LinkedIssue['type']) ?? 'task',
    subtaskId: raw.subtaskId != null ? String(raw.subtaskId) : undefined,
    labels: Array.isArray(raw.labels) ? (raw.labels as string[]) : [],
    assigneeIds: Array.isArray(raw.assigneeIds) ? (raw.assigneeIds as string[]) : [],
    linkedAt: String(raw.linkedAt ?? ''),
    linkedBy: String(raw.linkedBy ?? ''),
    resolvedAt: raw.resolvedAt != null ? String(raw.resolvedAt) : undefined,
    resolvedBy: raw.resolvedBy != null ? String(raw.resolvedBy) : undefined,
  };
}

function mapRawToDueDateChange(raw: Record<string, unknown>): DueDateChange {
  return {
    id: String(raw.id ?? ''),
    previousDueDate: String(raw.previousDueDate ?? ''),
    newDueDate: String(raw.newDueDate ?? ''),
    reason: String(raw.reason ?? ''),
    changedAt: String(raw.changedAt ?? ''),
    changedBy: String(raw.changedBy ?? ''),
    delayDays: Number(raw.delayDays ?? 0),
  };
}

function mapRawToTask(raw: Record<string, unknown>): Task {
  const raci = (raw.raci as TaskRACI) ?? defaultRaci;
  return {
    id: String(raw.id ?? ''),
    taskRef: raw.taskRef != null ? String(raw.taskRef) : undefined,
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    status: (raw.status as TaskStatus) ?? ('not_started' as TaskStatus),
    priority: (raw.priority as Task['priority']) ?? 'p3',
    projectId: raw.projectId != null ? String(raw.projectId) : undefined,
    milestoneId: raw.milestoneId != null ? String(raw.milestoneId) : undefined,
    parentTaskId: raw.parentTaskId != null ? String(raw.parentTaskId) : undefined,
    category: raw.category as Task['category'],
    raci: Array.isArray(raci.responsible) && (raci.accountable !== undefined || Array.isArray(raci.consulted))
      ? raci
      : defaultRaci,
    startDate: raw.startDate != null ? String(raw.startDate) : undefined,
    dueDate: String(raw.dueDate ?? ''),
    originalDueDate: raw.originalDueDate != null ? String(raw.originalDueDate) : undefined,
    totalDelayDays: raw.totalDelayDays != null ? Number(raw.totalDelayDays) : undefined,
    rescheduledFromDates: Array.isArray(raw.rescheduledFromDates) ? (raw.rescheduledFromDates as string[]) : [],
    wasRescheduledToday: Boolean(raw.wasRescheduledToday),
    dueDateHistory: Array.isArray(raw.dueDateHistory)
      ? (raw.dueDateHistory as Record<string, unknown>[]).map(mapRawToDueDateChange)
      : undefined,
    completedAt: raw.completedAt != null ? String(raw.completedAt) : undefined,
    estimatedMinutes: raw.estimatedMinutes != null ? Number(raw.estimatedMinutes) : undefined,
    actualMinutes: raw.actualMinutes != null ? Number(raw.actualMinutes) : undefined,
    order: Number(raw.order ?? 0),
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
    blockedByTaskIds: Array.isArray(raw.blockedByTaskIds) ? (raw.blockedByTaskIds as string[]) : [],
    blockingTaskIds: Array.isArray(raw.blockingTaskIds) ? (raw.blockingTaskIds as string[]) : [],
    subtaskIds: Array.isArray(raw.subtaskIds) ? (raw.subtaskIds as string[]) : [],
    attachments: Array.isArray(raw.attachments)
      ? (raw.attachments as Record<string, unknown>[]).map((a) => ({
          id: String(a.id),
          name: String(a.name ?? ''),
          url: String(a.url ?? ''),
          type: String(a.type ?? ''),
          size: Number(a.size ?? 0),
          uploadedAt: String(a.uploadedAt ?? ''),
          uploadedBy: String(a.uploadedBy ?? ''),
        }))
      : [],
    requiresReview: Boolean(raw.requiresReview),
    reviewStatus: raw.reviewStatus as Task['reviewStatus'],
    reviewerId: raw.reviewerId != null ? String(raw.reviewerId) : undefined,
    // Checklist
    checklistItems: Array.isArray(raw.checklistItems)
      ? (raw.checklistItems as Record<string, unknown>[]).map((item) => ({
          id: String(item.id ?? ''),
          taskId: String(item.taskId ?? ''),
          label: String(item.label ?? ''),
          isChecked: Boolean(item.isChecked),
          checkedAt: item.checkedAt != null ? String(item.checkedAt) : undefined,
          checkedBy: item.checkedBy != null ? String(item.checkedBy) : undefined,
          order: Number(item.order ?? 0),
          createdAt: String(item.createdAt ?? ''),
          updatedAt: String(item.updatedAt ?? ''),
        }))
      : undefined,
    checklistProgress: raw.checklistProgress
      ? {
          completed: Number((raw.checklistProgress as any).completed ?? 0),
          total: Number((raw.checklistProgress as any).total ?? 0),
        }
      : undefined,
    // Recurrence
    isRecurring: raw.isRecurring != null ? Boolean(raw.isRecurring) : undefined,
    recurrenceParentId: raw.recurrenceParentId != null ? String(raw.recurrenceParentId) : undefined,
    recurrenceConfig: raw.recurrenceConfig
      ? {
          id: String((raw.recurrenceConfig as any).id ?? ''),
          parentTaskId: String((raw.recurrenceConfig as any).parentTaskId ?? ''),
          frequency: (raw.recurrenceConfig as any).frequency as RecurrenceFrequency,
          intervalValue: Number((raw.recurrenceConfig as any).intervalValue ?? 1),
          daysOfWeek: Array.isArray((raw.recurrenceConfig as any).daysOfWeek)
            ? ((raw.recurrenceConfig as any).daysOfWeek as number[])
            : undefined,
          dayOfMonth: (raw.recurrenceConfig as any).dayOfMonth != null
            ? Number((raw.recurrenceConfig as any).dayOfMonth)
            : undefined,
          monthOfYear: (raw.recurrenceConfig as any).monthOfYear != null
            ? Number((raw.recurrenceConfig as any).monthOfYear)
            : undefined,
          endType: (raw.recurrenceConfig as any).endType as RecurrenceEndType,
          endValue: (raw.recurrenceConfig as any).endValue != null
            ? String((raw.recurrenceConfig as any).endValue)
            : undefined,
          nextGenerationDate: (raw.recurrenceConfig as any).nextGenerationDate != null
            ? String((raw.recurrenceConfig as any).nextGenerationDate)
            : undefined,
          createdAt: String((raw.recurrenceConfig as any).createdAt ?? ''),
          updatedAt: String((raw.recurrenceConfig as any).updatedAt ?? ''),
          createdBy: String((raw.recurrenceConfig as any).createdBy ?? ''),
        }
      : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    createdBy: raw.createdBy != null ? String(raw.createdBy) : undefined,
    linkedIssues: Array.isArray(raw.linkedIssues)
      ? (raw.linkedIssues as Record<string, unknown>[]).map(mapRawToLinkedIssue)
      : undefined,
    // Enriched name fields from backend
    raciNames: raw.raciNames as Task['raciNames'],
    projectName: raw.projectName != null ? String(raw.projectName) : undefined,
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    updatedByName: raw.updatedByName != null ? String(raw.updatedByName) : undefined,
    reviewerName: raw.reviewerName != null ? String(raw.reviewerName) : undefined,
  };
}

function mapListResponse(raw: {
  items?: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  hasMore?: boolean;
}): PaginatedResponse<Task> {
  const items = Array.isArray(raw?.items)
    ? (raw.items as Record<string, unknown>[]).map(mapRawToTask)
    : [];
  return {
    items,
    total: Number(raw?.total ?? 0),
    page: Number(raw?.page ?? 1),
    pageSize: Number(raw?.pageSize ?? 20),
    totalPages: Number(raw?.totalPages ?? 1),
    hasMore: Boolean(raw?.hasMore),
  };
}

function buildListParams(filters?: TaskFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.status && filters.status.length) params.set('status', filters.status.join(','));
  if (filters?.projectId) params.set('projectId', filters.projectId);
  if (filters?.assigneeId) params.set('assigneeId', filters.assigneeId);
  return params;
}

export const taskService = {
  async getTasks(filters?: TaskFilters, page = 1, pageSize = 20): Promise<PaginatedResponse<Task>> {
    const params = buildListParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const url = `${API_ENDPOINTS.TASKS.LIST}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; total: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }>(response);
    return mapListResponse(data);
  },

  /**
   * Fetches current user's tasks with server-side pagination.
   * @param page 1-based page number.
   * @param pageSize Number of items per page.
   * @param filters Optional status, projectId, and raciRole for server-side filtering.
   * @returns Paginated list of tasks.
   */
  async getMyTasks(
    page = 1,
    pageSize = 20,
    filters?: Pick<TaskFilters, 'status' | 'projectId' | 'raciRole'>
  ): Promise<PaginatedResponse<Task>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (filters?.status && filters.status.length) params.set('status', filters.status.join(','));
    if (filters?.projectId) params.set('projectId', filters.projectId);
    if (filters?.raciRole) params.set('raciRole', filters.raciRole);
    const response = await apiClient.get(`${API_ENDPOINTS.TASKS.MY_TASKS}?${params.toString()}`);
    const data = unwrapResponse<{
      items: Record<string, unknown>[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasMore: boolean;
    }>(response);
    return mapListResponse(data);
  },

  /**
   * Fetches tasks for a specific user (manager/admin viewing team member tasks)
   * @param userId The user ID to fetch tasks for
   * @param page 1-based page number
   * @param pageSize Number of items per page
   * @param filters Optional status, projectId, and raciRole for server-side filtering
   * @returns Paginated list of tasks for the specified user
   */
  async getUserTasks(
    userId: string,
    page = 1,
    pageSize = 20,
    filters?: Pick<TaskFilters, 'status' | 'projectId' | 'raciRole'>
  ): Promise<PaginatedResponse<Task>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (filters?.status && filters.status.length) params.set('status', filters.status.join(','));
    if (filters?.projectId) params.set('projectId', filters.projectId);
    if (filters?.raciRole) params.set('raciRole', filters.raciRole);
    const response = await apiClient.get(`${API_ENDPOINTS.TASKS.LIST}/user/${userId}?${params.toString()}`);
    const data = unwrapResponse<{
      items: Record<string, unknown>[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasMore: boolean;
    }>(response);
    return mapListResponse(data);
  },

  async getTodaysTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.TODAY);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getOverdueTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.OVERDUE);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getUpcomingTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.UPCOMING);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getLiveTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.LIVE);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  // Daily Commitments - Pick tasks for today
  async pickTaskForToday(taskId: string): Promise<void> {
    await apiClient.post('/task_management/tasks/pick-for-today', { taskId });
  },

  async getTodaysPicks(): Promise<Task[]> {
    const response = await apiClient.get('/task_management/tasks/todays-picks');
    const data = unwrapResponse<any[]>(response);
    // Backend returns array of commitment objects with nested task
    return Array.isArray(data) ? data.map((item: any) => {
      if (item.task) {
        return mapRawToTask(item.task);
      }
      return mapRawToTask(item);
    }) : [];
  },

  async removeFromToday(taskId: string): Promise<void> {
    await apiClient.delete(`/task_management/tasks/remove-from-today/${taskId}`);
  },

  async getPerformanceSummary(days: number = 30): Promise<any> {
    const response = await apiClient.get(`/task_management/tasks/performance-summary?days=${days}`);
    return unwrapResponse(response);
  },

  async getTask(taskId: string): Promise<Task> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.GET(taskId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async createTask(taskData: Partial<Task>): Promise<Task> {
    const body: Record<string, unknown> = {
      title: taskData.title ?? 'Untitled',
      description: taskData.description,
      status: taskData.status ?? 'not_started',
      priority: taskData.priority ?? 'p3',
      projectId: taskData.projectId,
      milestoneId: taskData.milestoneId,
      parentTaskId: taskData.parentTaskId,
      category: taskData.category,
      startDate: taskData.startDate != null ? toDateOnly(taskData.startDate) : undefined,
      dueDate: toDateOnly(taskData.dueDate) ?? new Date().toISOString().slice(0, 10),
      estimatedMinutes: taskData.estimatedMinutes,
      raci: taskData.raci ?? defaultRaci,
      tags: taskData.tags ?? [],
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.CREATE, body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async updateTask(taskId: string, taskData: Partial<Task>): Promise<Task> {
    const body: Record<string, unknown> = {
      title: taskData.title,
      description: taskData.description,
      status: taskData.status,
      priority: taskData.priority,
      projectId: taskData.projectId,
      milestoneId: taskData.milestoneId,
      startDate: taskData.startDate != null ? toDateOnly(taskData.startDate) : undefined,
      dueDate: taskData.dueDate != null ? toDateOnly(taskData.dueDate) : undefined,
      estimatedMinutes: taskData.estimatedMinutes,
      actualMinutes: taskData.actualMinutes,
      category: taskData.category,
      originalDueDate: taskData.originalDueDate != null ? toDateOnly(taskData.originalDueDate) : undefined,
      totalDelayDays: taskData.totalDelayDays,
      rescheduledFromDates: taskData.rescheduledFromDates,
      dueDateHistory: taskData.dueDateHistory,
      raci: taskData.raci,
      isOnsite: taskData.isOnsite,
      onsiteLocation: taskData.onsiteLocation,
    };
    if (taskData.blockedByTaskIds !== undefined) body.blockedByTaskIds = taskData.blockedByTaskIds;
    if (taskData.blockingTaskIds !== undefined) body.blockingTaskIds = taskData.blockingTaskIds;
    const response = await apiClient.put(API_ENDPOINTS.TASKS.UPDATE(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async deleteTask(taskId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.DELETE(taskId));
  },

  async getTasksByProject(projectId: string): Promise<Task[]> {
    const result = await this.getTasks({ projectId }, 1, 500);
    return result.items;
  },

  async updateTaskStatus(taskId: string, status: TaskStatus): Promise<Task> {
    const response = await apiClient.patch(API_ENDPOINTS.TASKS.UPDATE_STATUS(taskId), { status });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async completeTask(taskId: string): Promise<Task> {
    return this.updateTaskStatus(taskId, 'completed' as TaskStatus);
  },

  async reorderTasks(taskIds: string[], projectId?: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.REORDER, { taskIds, projectId });
  },

  async getSubtasks(taskId: string): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.SUBTASKS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async createSubtask(parentTaskId: string, subtaskData: Partial<Task>): Promise<Task> {
    const body = {
      ...subtaskData,
      parentTaskId,
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.SUBTASKS(parentTaskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async getComments(taskId: string): Promise<TaskComment[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.COMMENTS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data)
      ? data.map((c) => ({
          id: String(c.id),
          taskId,
          content: String(c.content ?? ''),
          parentCommentId: c.parentCommentId != null ? String(c.parentCommentId) : undefined,
          mentions: Array.isArray(c.mentions) ? (c.mentions as string[]) : [],
          createdAt: String(c.createdAt ?? ''),
          updatedAt: c.updatedAt != null ? String(c.updatedAt) : undefined,
          createdBy: c.createdBy != null ? String(c.createdBy) : undefined,
          createdByName: c.createdByName != null ? String(c.createdByName) : undefined,
        }))
      : [];
  },

  async addComment(taskId: string, content: string, mentions?: string[], parentCommentId?: string): Promise<TaskComment> {
    const body: Record<string, unknown> = { content };
    if (parentCommentId) body.parentCommentId = parentCommentId;
    const response = await apiClient.post(API_ENDPOINTS.TASKS.ADD_COMMENT(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id),
      taskId,
      content: String(data.content ?? ''),
      parentCommentId: data.parentCommentId != null ? String(data.parentCommentId) : undefined,
      mentions: mentions ?? [],
      createdAt: String(data.createdAt ?? ''),
      createdBy: String(data.createdBy ?? ''),
    };
  },

  async getAttachments(taskId: string): Promise<Attachment[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.ATTACHMENTS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data)
      ? data.map((a) => ({
          id: String(a.id),
          name: String(a.name ?? ''),
          url: String(a.url ?? ''),
          type: String(a.type ?? ''),
          size: Number(a.size ?? 0),
          uploadedAt: String(a.uploadedAt ?? ''),
          uploadedBy: String(a.uploadedBy ?? ''),
        }))
      : [];
  },

  async uploadAttachment(
    taskId: string,
    payload: { name: string; url: string; type?: string; size?: number }
  ): Promise<{ id: string; url: string }> {
    const body = {
      name: payload.name,
      url: payload.url,
      type: payload.type ?? 'application/octet-stream',
      size: payload.size ?? 0,
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.UPLOAD_ATTACHMENT(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return { id: String(data.id), url: String(data.url ?? '') };
  },

  async deleteAttachment(taskId: string, attachmentId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.DELETE_ATTACHMENT(taskId, attachmentId));
  },

  async getLinkedIssues(taskId: string): Promise<LinkedIssue[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.ISSUES(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToLinkedIssue) : [];
  },

  async addLinkedIssue(taskId: string, payload: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>): Promise<LinkedIssue> {
    const body = {
      title: payload.title,
      description: payload.description,
      provider: payload.provider ?? 'internal',
      externalId: payload.externalId,
      externalUrl: payload.externalUrl,
      status: payload.status ?? 'open',
      priority: payload.priority ?? 'medium',
      type: payload.type ?? 'task',
      subtaskId: payload.subtaskId,
      labels: payload.labels ?? [],
      assigneeIds: payload.assigneeIds ?? [],
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.ISSUES(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToLinkedIssue(data);
  },

  async updateLinkedIssue(taskId: string, issueId: string, payload: Partial<LinkedIssue>): Promise<LinkedIssue> {
    const body: Record<string, unknown> = {};
    if (payload.title !== undefined) body.title = payload.title;
    if (payload.description !== undefined) body.description = payload.description;
    if (payload.status !== undefined) body.status = payload.status;
    if (payload.priority !== undefined) body.priority = payload.priority;
    if (payload.type !== undefined) body.type = payload.type;
    if (payload.subtaskId !== undefined) body.subtaskId = payload.subtaskId;
    if (payload.labels !== undefined) body.labels = payload.labels;
    if (payload.assigneeIds !== undefined) body.assigneeIds = payload.assigneeIds;
    if (payload.resolvedAt !== undefined) body.resolvedAt = payload.resolvedAt;
    if (payload.resolvedBy !== undefined) body.resolvedBy = payload.resolvedBy;
    const response = await apiClient.put(API_ENDPOINTS.TASKS.ISSUE(taskId, issueId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToLinkedIssue(data);
  },

  async deleteLinkedIssue(taskId: string, issueId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.ISSUE(taskId, issueId));
  },

  async assignTask(taskId: string, userId: string, role = 'responsible'): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.ASSIGN(taskId), { userId, role });
  },

  async unassignTask(taskId: string, userId: string, role = 'responsible'): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.UNASSIGN(taskId), { userId, role });
  },

  // ============================================================================
  // CHECKLIST METHODS
  // ============================================================================

  async getChecklistItems(taskId: string): Promise<{ items: ChecklistItem[]; progress: { completed: number; total: number } }> {
    const response = await apiClient.get(`/task_management/tasks/${taskId}/checklist`);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; progress: { completed: number; total: number } }>(response);
    const items = Array.isArray(data.items)
      ? data.items.map((item) => ({
          id: String(item.id ?? ''),
          taskId: String(item.taskId ?? ''),
          label: String(item.label ?? ''),
          isChecked: Boolean(item.isChecked),
          checkedAt: item.checkedAt != null ? String(item.checkedAt) : undefined,
          checkedBy: item.checkedBy != null ? String(item.checkedBy) : undefined,
          order: Number(item.order ?? 0),
          createdAt: String(item.createdAt ?? ''),
          updatedAt: String(item.updatedAt ?? ''),
        }))
      : [];
    return { items, progress: data.progress };
  },

  async addChecklistItem(taskId: string, label: string): Promise<ChecklistItem> {
    const response = await apiClient.post(`/task_management/tasks/${taskId}/checklist`, { label });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id ?? ''),
      taskId: String(data.taskId ?? ''),
      label: String(data.label ?? ''),
      isChecked: Boolean(data.isChecked),
      checkedAt: data.checkedAt != null ? String(data.checkedAt) : undefined,
      checkedBy: data.checkedBy != null ? String(data.checkedBy) : undefined,
      order: Number(data.order ?? 0),
      createdAt: String(data.createdAt ?? ''),
      updatedAt: String(data.updatedAt ?? ''),
    };
  },

  async updateChecklistItem(
    taskId: string,
    itemId: string,
    updates: { label?: string; isChecked?: boolean }
  ): Promise<ChecklistItem> {
    const response = await apiClient.put(`/task_management/tasks/${taskId}/checklist/${itemId}`, updates);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id ?? ''),
      taskId: String(data.taskId ?? ''),
      label: String(data.label ?? ''),
      isChecked: Boolean(data.isChecked),
      checkedAt: data.checkedAt != null ? String(data.checkedAt) : undefined,
      checkedBy: data.checkedBy != null ? String(data.checkedBy) : undefined,
      order: Number(data.order ?? 0),
      createdAt: String(data.createdAt ?? ''),
      updatedAt: String(data.updatedAt ?? ''),
    };
  },

  async deleteChecklistItem(taskId: string, itemId: string): Promise<void> {
    await apiClient.delete(`/task_management/tasks/${taskId}/checklist/${itemId}`);
  },

  async reorderChecklistItems(taskId: string, itemIds: string[]): Promise<void> {
    await apiClient.post(`/task_management/tasks/${taskId}/checklist/reorder`, { itemIds });
  },

  // ============================================================================
  // RECURRENCE METHODS
  // ============================================================================

  async createRecurrence(
    taskId: string,
    config: {
      frequency: RecurrenceFrequency;
      intervalValue: number;
      daysOfWeek?: number[];
      dayOfMonth?: number;
      monthOfYear?: number;
      endType: RecurrenceEndType;
      endValue?: string;
    }
  ): Promise<RecurrenceConfig> {
    const response = await apiClient.post(`/task_management/tasks/${taskId}/recurrence`, config);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id ?? ''),
      parentTaskId: String(data.parentTaskId ?? ''),
      frequency: data.frequency as RecurrenceFrequency,
      intervalValue: Number(data.intervalValue ?? 1),
      daysOfWeek: Array.isArray(data.daysOfWeek) ? (data.daysOfWeek as number[]) : undefined,
      dayOfMonth: data.dayOfMonth != null ? Number(data.dayOfMonth) : undefined,
      monthOfYear: data.monthOfYear != null ? Number(data.monthOfYear) : undefined,
      endType: data.endType as RecurrenceEndType,
      endValue: data.endValue != null ? String(data.endValue) : undefined,
      nextGenerationDate: data.nextGenerationDate != null ? String(data.nextGenerationDate) : undefined,
      createdAt: String(data.createdAt ?? ''),
      updatedAt: String(data.updatedAt ?? ''),
      createdBy: String(data.createdBy ?? ''),
    };
  },

  async getRecurrence(taskId: string): Promise<RecurrenceConfig | null> {
    try {
      const response = await apiClient.get(`/task_management/tasks/${taskId}/recurrence`);
      const data = unwrapResponse<Record<string, unknown>>(response);
      if (!data || Object.keys(data).length === 0) return null;
      return {
        id: String(data.id ?? ''),
        parentTaskId: String(data.parentTaskId ?? ''),
        frequency: data.frequency as RecurrenceFrequency,
        intervalValue: Number(data.intervalValue ?? 1),
        daysOfWeek: Array.isArray(data.daysOfWeek) ? (data.daysOfWeek as number[]) : undefined,
        dayOfMonth: data.dayOfMonth != null ? Number(data.dayOfMonth) : undefined,
        monthOfYear: data.monthOfYear != null ? Number(data.monthOfYear) : undefined,
        endType: data.endType as RecurrenceEndType,
        endValue: data.endValue != null ? String(data.endValue) : undefined,
        nextGenerationDate: data.nextGenerationDate != null ? String(data.nextGenerationDate) : undefined,
        createdAt: String(data.createdAt ?? ''),
        updatedAt: String(data.updatedAt ?? ''),
        createdBy: String(data.createdBy ?? ''),
      };
    } catch (error: any) {
      // Return null for 404 (no recurrence) or 403 (no access)
      if (error?.response?.status === 404 || error?.response?.status === 403) return null;
      // For other errors, log but still return null to prevent UI breaking
      console.warn('Error fetching recurrence config:', error?.message);
      return null;
    }
  },

  async updateRecurrence(
    taskId: string,
    recurrenceId: string,
    updates: Partial<{
      frequency: RecurrenceFrequency;
      intervalValue: number;
      daysOfWeek: number[];
      dayOfMonth: number;
      monthOfYear: number;
      endType: RecurrenceEndType;
      endValue: string;
    }>
  ): Promise<RecurrenceConfig> {
    const response = await apiClient.put(`/task_management/tasks/${taskId}/recurrence/${recurrenceId}`, updates);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id ?? ''),
      parentTaskId: String(data.parentTaskId ?? ''),
      frequency: data.frequency as RecurrenceFrequency,
      intervalValue: Number(data.intervalValue ?? 1),
      daysOfWeek: Array.isArray(data.daysOfWeek) ? (data.daysOfWeek as number[]) : undefined,
      dayOfMonth: data.dayOfMonth != null ? Number(data.dayOfMonth) : undefined,
      monthOfYear: data.monthOfYear != null ? Number(data.monthOfYear) : undefined,
      endType: data.endType as RecurrenceEndType,
      endValue: data.endValue != null ? String(data.endValue) : undefined,
      nextGenerationDate: data.nextGenerationDate != null ? String(data.nextGenerationDate) : undefined,
      createdAt: String(data.createdAt ?? ''),
      updatedAt: String(data.updatedAt ?? ''),
      createdBy: String(data.createdBy ?? ''),
    };
  },

  async deleteRecurrence(taskId: string, recurrenceId: string): Promise<void> {
    await apiClient.delete(`/task_management/tasks/${taskId}/recurrence/${recurrenceId}`);
  },

  async getRecurrenceInstances(
    taskId: string,
    filters?: { startDate?: string; endDate?: string; limit?: number }
  ): Promise<RecurrenceInstance[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.limit) params.set('limit', String(filters.limit));
      const url = `/task_management/tasks/${taskId}/recurrence/instances${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      const data = unwrapResponse<Record<string, unknown>[]>(response);
      return Array.isArray(data)
        ? data.map((item) => ({
            id: String(item.id ?? ''),
            recurrenceId: String(item.recurrenceId ?? ''),
            taskId: String(item.taskId ?? ''),
            instanceDate: String(item.instanceDate ?? ''),
            isException: Boolean(item.isException),
            createdAt: String(item.createdAt ?? ''),
          }))
        : [];
    } catch (error: any) {
      // Return empty array for 404 (no instances) or 403 (no access)
      if (error?.response?.status === 404 || error?.response?.status === 403) return [];
      // For other errors, log but still return empty array to prevent UI breaking
      console.warn('Error fetching recurrence instances:', error?.message);
      return [];
    }
  },
};

export default taskService;
